// src/pages/coupons/CouponsTable.jsx
import React, { useEffect, useMemo, useState, memo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { Link } from "react-router-dom";
import { FiEdit3, FiTrash2, FiEye } from "react-icons/fi";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";

/* ---- helpers ---- */
const fmt = (n) =>
  n == null
    ? "-"
    : Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const HeaderCell = memo(function HeaderCell({ couponId, initial, patchRow }) {
  const [isChecked, setIsChecked] = useState(!!initial);
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    const next = !isChecked;

    setIsChecked(next);
    setBusy(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/coupons/${couponId}`,
        { is_header: next ? 1 : 0 }, //  API update
        { headers: { Authorization: `Bearer ${token}` } }
      );
      patchRow?.(couponId, { is_header: next ? 1 : 0 });
    } catch (err) {
      console.error("❌ Header bilgisi güncellenemedi:", err);
      setIsChecked(!next);
      toast.error("Header bilgisi güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="form-check form-switch text-start d-flex align-items-start justify-content-start"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggle}
        disabled={busy}
        style={{ cursor: busy ? "not-allowed" : "pointer", width: "45px", height: "22px" }}
      />
    </div>
  );
});

/* ---- Aktif toggle hücresi ---- */
const ActiveCell = memo(function ActiveCell({ couponId, initial, patchRow }) {
  const [isChecked, setIsChecked] = useState(!!initial);
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    const next = !isChecked;

    // optimistik
    setIsChecked(next);
    setBusy(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/coupons/${couponId}/active`,
        { is_active: next ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // sadece bu satırı lokalde güncelle
      patchRow?.(couponId, { is_active: next ? 1 : 0 });
    } catch (err) {
      console.error("❌ Aktiflik güncellenemedi:", err);
      setIsChecked(!next); // geri al
      toast.error("Aktiflik güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="form-check form-switch text-start d-flex align-items-start justify-content-start"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggle}
        disabled={busy}
        style={{
          cursor: busy ? "not-allowed" : "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
    </div>
  );
});

export default function CouponsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const patchRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/coupons`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      setRows(res.data || []);
    } catch (e) {
      console.error("❌ Kupon listesi alınamadı:", e);
      toast.error("Kupon listesi alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleDelete = (id, code) => {
    confirmAlert({
      title: "Silinsin mi?",
      message: `${code} kodlu kupon silinecek. Bu işlem geri alınamaz.`,
      buttons: [
        {
          label: "Evet, sil",
          onClick: async () => {
            try {
              await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/coupons/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
              });
              toast.success("Kupon silindi");
              setRows((prev) => prev.filter((r) => r.id !== id));
            } catch (e) {
              console.error("❌ Silme hatası:", e);
              toast.error("Kupon silinemedi");
            }
          },
        },
        { label: "İptal" },
      ],
    });
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: () => "ID", size: 70 },
      {
        accessorKey: "code",
        header: () => "Kod",
        size: 180,
        cell: (info) => {
          const r = info.row.original;
          return (
            <Link className="text-decoration-none" to={`/coupons/edit/${r.id}`}>
              {r.code}
            </Link>
          );
        },
      },
      {
        accessorKey: "discount_type",
        header: () => "Tip",
        size: 110,
        cell: (i) => (String(i.getValue()) === "fixed" ? "Tutar" : "Yüzde"),
      },
      {
        id: "discount_value_pretty",
        header: () => "İndirim",
        size: 130,
        cell: (i) => {
          const r = i.row.original;
          return r.discount_type === "percent" ? `%${r.discount_value}` : `${fmt(r.discount_value)} ₺`;
        },
      },
      {
        accessorKey: "min_amount",
        header: () => "Min. Tutar",
        size: 140,
        cell: (i) => (i.getValue() == null ? "-" : `${fmt(i.getValue())} ₺`),
      },
      { accessorKey: "usage_limit", header: () => "Limit", size: 110, cell: (i) => i.getValue() ?? "-" },
      { accessorKey: "usage_count", header: () => "Kullanım", size: 110, cell: (i) => i.getValue() ?? 0 },

      /* ---- Aktif toggle sütunu ---- */
      {
        id: "is_active",
        header: () => <div style={{ textAlign: "start", width: 120 }}>Aktif</div>,
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 120,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div style={{ textAlign: "start", width: 120 }}>
              <ActiveCell couponId={row.id} initial={row.is_active} patchRow={patchRow} />
            </div>
          );
        },
      },
      {
  id: "is_header",
  header: () => <div style={{ textAlign: "start", width: 150 }}>Header’da Göster</div>,
  size: 150,
  enableSorting: false,
  cell: (info) => {
    const row = info.row.original;
    return (
      <div style={{ textAlign: "start", width: 150 }}>
        <HeaderCell couponId={row.id} initial={row.is_header} patchRow={patchRow} />
      </div>
    );
  },
},


      {
        accessorKey: "start_at",
        header: () => "Başlangıç",
        size: 170,
        cell: (i) => (i.getValue() ? new Date(i.getValue()).toLocaleString("tr-TR") : "-"),
      },
      {
        accessorKey: "end_at",
        header: () => "Bitiş",
        size: 170,
        cell: (i) => (i.getValue() ? new Date(i.getValue()).toLocaleString("tr-TR") : "-"),
      },
      {
        id: "actions",
        header: () => "İşlemler",
        size: 110,
        enableSorting: false,
        cell: (i) => {
          const r = i.row.original;
          return (
            <div className="hstack gap-2 justify-content-end">
               <Link
          to={`/coupon/view/${r.id}`}
          className="avatar-text avatar-md text-info bg-soft-info border-soft-info"
          title="Görüntüle"
        >
          <FiEye />
        </Link>
              <Link
                to={`/coupon/edit/${r.id}`}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title="Düzenle"
              >
                <FiEdit3 />
              </Link>
              <button
                type="button"
                onClick={() => handleDelete(r.id, r.code)}
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title="Sil"
              >
                <FiTrash2 />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="container py-3">


      {loading ? <div className="alert alert-light">Yükleniyor…</div> : <Table data={rows} columns={columns} />}
    </div>
  );
}
